import { type SettingsFlow } from '@ory/client-fetch';
import { Settings } from '@ory/elements-react/theme';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { oryComponentOverrides } from '@apps/web/components/auth/ory_components';
import { useOry } from '@apps/web/hooks/ory';

export const Route = createFileRoute('/_auth/change-password')({
    component: ChangePasswordPage,
    validateSearch: z.object({
        flow: z.string().optional(),
    }),
});

// Where the settings flow says to send the customer once the password is set,
// when it is one of our own pages. Kratos carries `return_to` from the minted
// account setup link through the recovery flow onto this settings flow, and it
// only ever stores a URL it already matched against its allowed return URLs;
// re-checking the origin here keeps a misconfigured allow-list from turning
// into an open redirect.
function sameOriginReturnTo(returnTo: string | undefined): string | null {
    if (!returnTo) {
        return null;
    }
    try {
        return new URL(returnTo).origin === window.location.origin
            ? returnTo
            : null;
    } catch {
        return null;
    }
}

export function ChangePasswordPage() {
    const { flow: flowId } = Route.useSearch();
    const router = useRouter();
    const [flow, setFlow] = useState<SettingsFlow | null>(null);

    const { oryClient, oryElementsConfiguration, normalizeOryPayload } =
        useOry();

    useEffect(() => {
        (async () => {
            try {
                if (flowId) {
                    const flow = normalizeOryPayload(
                        await oryClient.getSettingsFlow({
                            id: flowId,
                        }),
                    );
                    setFlow(flow);
                } else {
                    const flow = normalizeOryPayload(
                        await oryClient.createBrowserSettingsFlow(),
                    );
                    router.navigate({
                        to: '/change-password',
                        search: (prev) => ({ ...prev, flow: flow.id }),
                    });
                }
            } catch (error) {
                console.error(error);
            }
        })();
    }, [flowId, normalizeOryPayload, oryClient, router]);

    useEffect(() => {
        if (flow?.state !== 'success') {
            return;
        }
        const returnTo = sameOriginReturnTo(flow.return_to);
        if (returnTo) {
            // A full page load, not a router navigation: the organization
            // provider reads `?organizationId=` off the initial URL when it
            // mounts, so navigating in-app would drop the organization the
            // link was scoped to.
            window.location.assign(returnTo);
            return;
        }
        router.navigate({ to: '/' });
    }, [flow, router]);

    if (!flow) {
        return null;
    }

    return (
        <div className="ory-root w-full max-w-xl">
            <Settings
                flow={flow}
                config={oryElementsConfiguration}
                components={{
                    ...oryComponentOverrides,
                    Page: {
                        // Remove header which has a superfluous dropdown menu.
                        Header: () => null,
                    },
                }}
            />
        </div>
    );
}
